import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, ArrowUpRight, Phone, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { PORTAL_URL, PHONE, PHONE_HREF } from "@/lib/api";
import { RuneMark } from "./Img";
import { useFetch } from "@/hooks/useFetch";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/ecu-remapping", label: "ECU Remapping" },
  { to: "/services", label: "Services" },
  { to: "/remappingfranchise", label: "Become a Dealer" },
  { to: "/tuning-files", label: "Tuning Files" },
  {
    to: "/tuning-tools", label: "Tuning Tools", children: [
      { to: "/tuning-tools/alientech", label: "Alientech KESS3", note: "OBD · bench · boot" },
      { to: "/tuning-tools/autotuner", label: "Autotuner", note: "No annual subscription" },
      { to: "/tuning-tools/dimsport", label: "Dimsport", note: "New Genius · Trasdata" },
      { to: "/tuning-tools/flex", label: "Magic Motorsport FLEX", note: "OBD · bench" },
      { to: "/tuning-tools/cmd", label: "CMD Flashtec", note: "Supplied to order" },
    ],
  },
  { to: "/shop", label: "Shop" },
  { to: "/software-downloads", label: "Downloads" },
  { to: "/blog", label: "Guides & News" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function useNav() {
  const { data: site } = useFetch("/site");
  const hidden = site?.hidden_pages || [];
  return NAV.map((n) => (n.children ? { ...n, children: n.children.filter((c) => !hidden.includes(`tools-${c.to.split("/").pop()}`)) } : n));
}

export function PortalButton({ className = "", testId = "customer-portal-button", label = "Customer Portal" }) {
  return (
    <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" data-testid={testId}
      className={`group inline-flex items-center gap-2 border border-[color:var(--ice)] px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-[color:var(--ice)] transition-colors duration-300 hover:bg-[color:var(--ice)] hover:text-[#071018] ${className}`}>
      {label} <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </a>
  );
}

function ToolsMenu({ item }) {
  return (
    <div className="group relative">
      <NavLink to={item.to} data-testid="nav-tuning-tools"
        className={({ isActive }) => `flex items-center gap-1 whitespace-nowrap px-2 py-2 text-[13px] font-medium transition-colors ${isActive ? "text-[color:var(--ice)]" : "text-slate-300 hover:text-white"}`}>
        {item.label} <ChevronDown className="h-3 w-3 transition-transform duration-300 group-hover:rotate-180" />
      </NavLink>
      <div className="invisible absolute left-1/2 top-full z-50 w-[420px] -translate-x-1/2 translate-y-2 pt-3 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="panel relative overflow-hidden p-2 shadow-2xl shadow-black/60">
          <div className="knot-bg pointer-events-none absolute inset-0 opacity-[0.05]" />
          {item.children.map((c) => (
            <Link key={c.to} to={c.to} data-testid={`nav-tools-${c.to.split("/").pop()}`}
              className="relative flex items-center justify-between px-4 py-3 transition-colors hover:bg-white/[0.03]">
              <span className="font-display text-lg uppercase tracking-wide text-slate-100">{c.label}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{c.note}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileNav({ open, onClose }) {
  const nav = useNav();
  return (
    <div className={`fixed inset-0 z-[60] lg:hidden ${open ? "" : "pointer-events-none"}`} data-testid="mobile-nav">
      <div className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
      <nav className={`absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-[#0d1014] border-l border-[color:var(--line)] transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between border-b border-[color:var(--line)] px-5 py-4">
          <span className="eyebrow">Menu</span>
          <button onClick={onClose} aria-label="Close menu" data-testid="mobile-nav-close" className="p-2 text-slate-300"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {nav.map((n, i) => (
            <div key={n.to} className="border-b border-white/[0.04]">
              <NavLink to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) => `flex items-baseline gap-3 py-3.5 font-display text-2xl uppercase tracking-wide ${isActive ? "text-[color:var(--ice)]" : "text-slate-100"}`}>
                <span className="font-mono text-[10px] text-slate-600">{String(i + 1).padStart(2, "0")}</span>{n.label}
              </NavLink>
              {n.children && (
                <div className="mb-3 ml-8 grid grid-cols-1 gap-1">
                  {n.children.map((c) => (
                    <Link key={c.to} to={c.to} className="py-1.5 text-sm text-slate-400 hover:text-white">{c.label}</Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="space-y-3 border-t border-[color:var(--line)] p-5">
          <PortalButton className="w-full justify-center py-3" testId="mobile-customer-portal-button" />
          <a href={PHONE_HREF} className="flex items-center justify-center gap-2 py-2 font-mono text-sm text-slate-300"><Phone className="h-4 w-4" /> {PHONE}</a>
        </div>
      </nav>
    </div>
  );
}

export function Header() {
  const nav = useNav();
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => setMobile(false), [loc.pathname]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 12);
    f();
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <>
      <div className="hidden border-b border-[color:var(--line)] bg-[#08090b] lg:block">
        <div className="container-x flex h-9 items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
          <span>Skipton · North Yorkshire · UK &amp; worldwide dealers</span>
          <a href={PHONE_HREF} className="flex items-center gap-2 text-slate-300 hover:text-[color:var(--ice)]" data-testid="topbar-phone"><Phone className="h-3 w-3" /> {PHONE}</a>
        </div>
      </div>
      <header className={`sticky top-0 z-50 border-b backdrop-blur-xl transition-colors duration-300 ${scrolled ? "border-[color:var(--line)] bg-[#0b0d10]/92" : "border-transparent bg-[#0b0d10]/80"}`} data-testid="site-header">
        <div className="container-x flex h-16 items-center justify-between gap-4 lg:h-[72px]">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <RuneMark className="h-8 w-8 text-[color:var(--ice)]" />
            <span className="leading-none">
              <span className="block font-display text-xl font-bold uppercase tracking-[0.18em] text-white">Jorvik</span>
              <span className="block font-mono text-[9px] uppercase tracking-[0.42em] text-[color:var(--bronze)]">Tuning</span>
            </span>
          </Link>
          <nav className="hidden items-center xl:flex" aria-label="Main">
            {nav.map((n) => n.children ? <ToolsMenu key={n.to} item={n} /> : (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) => `relative whitespace-nowrap px-2 py-2 text-[13px] font-medium transition-colors ${isActive ? "text-[color:var(--ice)]" : "text-slate-300 hover:text-white"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <PortalButton className="hidden sm:inline-flex" />
            <PortalButton className="!px-2.5 !py-1.5 !text-[10px] sm:hidden" label="Portal" testId="header-portal-mobile" />
            <button onClick={() => setOpen(true)} className="relative p-2.5 text-slate-200 transition-colors hover:text-[color:var(--ice)]" aria-label="Open basket" data-testid="header-cart-button">
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center bg-[color:var(--bronze)] px-1 font-mono text-[10px] font-bold text-black" data-testid="header-cart-count">{count}</span>}
            </button>
            <button onClick={() => setMobile(true)} className="p-2.5 text-slate-200 xl:hidden" aria-label="Open menu" data-testid="mobile-nav-toggle"><Menu className="h-6 w-6" /></button>
          </div>
        </div>
      </header>
      <MobileNav open={mobile} onClose={() => setMobile(false)} />
    </>
  );
}
